import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
} from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { Box } from '@mui/material';
import { MediaItem } from './media-item';
import type { MediaDto } from '@/types/dtos/media/media.dto.ts';
import type { MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum.ts';
import type { PlaylistMediaRow } from '@/helpers/flow-media-playlist.helper';
import { useReorderMediaPlaylistMutation } from '@/state/mutations/media/use-reorder-media-playlist.ts';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';
import type { MediaListDisplayVariant } from '@/components/flows/detail/flow-detail.types';

const DRAG_HANDLE_SIZE_PX = 22;
/** Match `SwipeableContributorItem` / session contributor list row spacing. */
const SORTABLE_ROW_MARGIN = '0.4rem 0';

const playlistRowsOrderKey = (rows: PlaylistMediaRow[]) => rows.map((row) => row.usageId).join('\0');

type SortableMediaRowProps = {
  usageId: string;
  showDragHandle: boolean;
  sortableDisabled: boolean;
  children: (dragHandle: ReactNode | undefined) => ReactNode;
};

const PlaylistDragHandle = ({
  isDragging,
  interactionDisabled,
  attributes,
  listeners,
}: {
  isDragging: boolean;
  interactionDisabled: boolean;
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
}) => {
  const t = useTranslations();

  return (
    <Box
      component="button"
      type="button"
      aria-label={t('media.reorderDragHandleAria')}
      sx={{
        'flexShrink': 0,
        'display': 'inline-flex',
        'alignItems': 'center',
        'justifyContent': 'center',
        'width': DRAG_HANDLE_SIZE_PX,
        'minWidth': DRAG_HANDLE_SIZE_PX,
        'height': DRAG_HANDLE_SIZE_PX,
        'p': 0,
        'm': 0,
        'border': 0,
        'borderRadius': 0.5,
        'bgcolor': 'transparent',
        'color': 'action.disabled',
        'cursor': interactionDisabled ? 'default' : isDragging ? 'grabbing' : 'grab',
        'pointerEvents': interactionDisabled ? 'none' : 'auto',
        'touchAction': 'none',
        'WebkitTapHighlightColor': 'transparent',
        '&:hover': { color: 'action.active', bgcolor: 'action.hover' },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 1,
        },
      }}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
    >
      <DragHandleIcon sx={{ fontSize: 16, transform: 'rotate(90deg)' }} />
    </Box>
  );
};

const SortableMediaRow = ({ usageId, showDragHandle, sortableDisabled, children }: SortableMediaRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: usageId,
    disabled: sortableDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const dragHandle = showDragHandle ? (
    <PlaylistDragHandle
      isDragging={isDragging}
      interactionDisabled={sortableDisabled}
      attributes={attributes}
      listeners={listeners}
    />
  ) : undefined;

  return (
    <Box ref={setNodeRef} style={style} sx={{ minWidth: 0, m: SORTABLE_ROW_MARGIN }}>
      {children(dragHandle)}
    </Box>
  );
};

export const SortablePlaylistMediaList = ({
  rows: initialRows,
  playlistId,
  canReorder,
  canEditPlaylistAdvanceMode = false,
  parentId,
  parentType,
  contextParentId,
  contextParentType,
  isItemSelected,
  connectedPlayer,
  playersPlayingType,
  displayVariant = 'default',
  hidePerRowSlideControl = false,
  contributorFacing = false,
}: {
  rows: PlaylistMediaRow[];
  playlistId: string;
  canReorder: boolean;
  /** Advance / auto-loop / show-mode — requires MediaPlaylist manage, not reorder. */
  canEditPlaylistAdvanceMode?: boolean;
  parentId: string;
  parentType: MediaParentEntity;
  contextParentId: string;
  contextParentType: MediaParentEntity;
  isItemSelected?: (media: MediaDto) => boolean;
  connectedPlayer?: {
    currentSlideIndex?: number;
    totalSlides?: number;
    speakerNotes?: string;
    state?: { page?: string };
  };
  playersPlayingType?: string;
  displayVariant?: MediaListDisplayVariant;
  hidePerRowSlideControl?: boolean;
  contributorFacing?: boolean;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const [rows, setRows] = useState(initialRows);
  /** While set, ignore parent `initialRows` until server/cache order matches (slot-list `isDragOptimistic` pattern). */
  const optimisticOrderKeyRef = useRef<string | null>(null);
  const { mutateAsync: reorderPlaylist, isPending: isReordering } = useReorderMediaPlaylistMutation();

  useEffect(() => {
    const incomingKey = playlistRowsOrderKey(initialRows);
    if (optimisticOrderKeyRef.current !== null) {
      if (incomingKey === optimisticOrderKeyRef.current) {
        optimisticOrderKeyRef.current = null;
      }
      return;
    }
    setRows(initialRows);
  }, [initialRows]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || isReordering) {
        return;
      }

      const oldIndex = rows.findIndex((row) => row.usageId === active.id);
      const newIndex = rows.findIndex((row) => row.usageId === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }

      const nextRows = arrayMove(rows, oldIndex, newIndex);
      const previousRows = rows;
      const nextOrderKey = playlistRowsOrderKey(nextRows);
      optimisticOrderKeyRef.current = nextOrderKey;
      setRows(nextRows);

      try {
        await reorderPlaylist({
          playlistId,
          refs: nextRows.map((row, index) => ({
            usageId: row.usageId,
            sequence: index + 1,
          })),
        });
      } catch {
        optimisticOrderKeyRef.current = null;
        setRows(previousRows);
        notification.notifyError(t('media.notification.reorderError'));
      }
    },
    [rows, isReordering, playlistId, reorderPlaylist, notification, t],
  );

  const showDragHandle = canReorder && rows.length > 1;
  const sortableDisabled = !showDragHandle || isReordering;
  const sortableIds = rows.map((row) => row.usageId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={(event) => {
        void handleDragEnd(event);
      }}
    >
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        {rows.map((row) => (
          <SortableMediaRow
            key={row.usageId}
            usageId={row.usageId}
            showDragHandle={showDragHandle}
            sortableDisabled={sortableDisabled}
          >
            {(dragHandle) => (
              <MediaItem
                mediaItem={row.media}
                parentId={parentId}
                parentType={parentType}
                contextParentId={contextParentId}
                contextParentType={contextParentType}
                selected={isItemSelected?.(row.media) ?? false}
                playersPlayingType={playersPlayingType}
                currentSlideIndex={connectedPlayer?.currentSlideIndex}
                totalSlides={connectedPlayer?.totalSlides}
                speakerNotes={connectedPlayer?.speakerNotes}
                disableListItemMargin
                dragHandle={dragHandle}
                playlistId={playlistId}
                playlistUsageId={row.usageId}
                playlistAdvanceMode={row.advanceMode}
                playlistNextShowMode={row.nextShowMode}
                playlistLoopEnabled={row.loopEnabled}
                playlistDwellMs={row.dwellMs}
                canEditPlaylistAdvanceMode={canEditPlaylistAdvanceMode}
                displayVariant={displayVariant}
                hidePerRowSlideControl={hidePerRowSlideControl}
                contributorFacing={contributorFacing}
              />
            )}
          </SortableMediaRow>
        ))}
      </SortableContext>
    </DndContext>
  );
};
